import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
  Cloud, HardDrive, Database, Download, Trash2, RefreshCw, 
  AlertTriangle, CheckCircle2, Clock, Archive, Play, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';

const SuperadminStorageManagement = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [backups, setBackups] = useState([]);
  const [cancelledAccounts, setCancelledAccounts] = useState([]);
  const [storageTest, setStorageTest] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [backupInProgress, setBackupInProgress] = useState(false);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'overview') {
        const response = await api.get('/superadmin/storage/overview');
        setOverview(response.data);
      } else if (activeTab === 'backups') {
        const response = await api.get('/superadmin/backups');
        setBackups(response.data.backups || []);
      } else if (activeTab === 'cancelled') {
        const response = await api.get('/superadmin/storage/cancelled-accounts');
        setCancelledAccounts(response.data.cancelled_accounts || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const testStorage = async () => {
    try {
      const response = await api.get('/superadmin/storage/test');
      setStorageTest(response.data);
      
      if (response.data.gcs_connection?.success && response.data.encryption?.success) {
        toast.success('Storage and encryption tests passed!');
      } else {
        toast.warning('Some tests failed. Check details.');
      }
    } catch (error) {
      toast.error('Storage test failed');
    }
  };

  const createBackup = async () => {
    setBackupInProgress(true);
    try {
      await api.post('/superadmin/backups/create');
      toast.success('Backup started! This may take a few minutes.');
      
      // Refresh after delay
      setTimeout(fetchData, 5000);
    } catch (error) {
      toast.error('Failed to start backup');
    } finally {
      setBackupInProgress(false);
    }
  };

  const deleteAccountData = async () => {
    if (!selectedAccount) return;
    
    try {
      await api.delete(`/superadmin/storage/cancelled/${selectedAccount.company_id}`);
      toast.success('Account data deleted');
      setShowDeleteModal(false);
      setSelectedAccount(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete account data');
    }
  };

  const downloadBackup = async (backupName) => {
    try {
      const response = await api.get(`/superadmin/backups/${backupName}/download`);
      
      if (response.data.download_instructions) {
        toast.info(response.data.download_instructions, { duration: 8000 });
      }
      
      if (response.data.gcs_path) {
        toast.success(`Backup located at: ${response.data.gcs_path}`);
      }
    } catch (error) {
      toast.error('Failed to get backup download info');
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 GB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="space-y-6" data-testid="superadmin-storage">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Storage & Backups</h1>
          <p className="text-gray-500">Manage cloud storage, backups, and data retention</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={testStorage}>
            <Shield className="mr-2 h-4 w-4" />
            Test Storage
          </Button>
          <Button onClick={fetchData}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Storage Test Results */}
      {storageTest && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                {storageTest.gcs_connection?.success ? (
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                )}
                <span>GCS Connection: {storageTest.gcs_connection?.success ? 'OK' : 'Failed'}</span>
              </div>
              <div className="flex items-center gap-2">
                {storageTest.encryption?.success ? (
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                )}
                <span>Encryption: {storageTest.encryption?.success ? 'OK' : 'Failed'}</span>
              </div>
              {storageTest.gcs_connection?.bucket_name && (
                <Badge variant="outline">Bucket: {storageTest.gcs_connection.bucket_name}</Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">
            <Cloud className="mr-2 h-4 w-4" />
            Storage Overview
          </TabsTrigger>
          <TabsTrigger value="backups">
            <Database className="mr-2 h-4 w-4" />
            Backups
          </TabsTrigger>
          <TabsTrigger value="cancelled">
            <Archive className="mr-2 h-4 w-4" />
            Cancelled Accounts
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-2">
                      <HardDrive className="h-8 w-8 text-blue-500" />
                      <div>
                        <p className="text-2xl font-bold">{overview?.total_used_gb || 0} GB</p>
                        <p className="text-sm text-gray-500">Total Storage Used</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-2">
                      <Cloud className="h-8 w-8 text-green-500" />
                      <div>
                        <p className="text-2xl font-bold">{overview?.total_companies || 0}</p>
                        <p className="text-sm text-gray-500">Companies Using Storage</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-2">
                      <Shield className="h-8 w-8 text-purple-500" />
                      <div>
                        <p className="text-2xl font-bold">AES-256</p>
                        <p className="text-sm text-gray-500">Encryption Standard</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Company Storage Table */}
              <Card>
                <CardHeader>
                  <CardTitle>Storage by Company</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Company</TableHead>
                        <TableHead>Plan</TableHead>
                        <TableHead>Used</TableHead>
                        <TableHead>Limit</TableHead>
                        <TableHead>Usage</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {overview?.companies?.map((company) => (
                        <TableRow key={company.company_id}>
                          <TableCell className="font-medium">{company.company_name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{company.plan_type}</Badge>
                          </TableCell>
                          <TableCell>{company.used_gb} GB</TableCell>
                          <TableCell>{company.limit_gb} GB</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Progress 
                                value={company.percentage_used} 
                                className="w-20 h-2"
                              />
                              <span className={`text-sm ${company.percentage_used >= 90 ? 'text-red-500' : ''}`}>
                                {company.percentage_used}%
                              </span>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Backups Tab */}
        <TabsContent value="backups" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-gray-500">Daily backups run at 2 AM UK time. Last 7 backups are kept.</p>
            <Button onClick={createBackup} disabled={backupInProgress}>
              <Play className="mr-2 h-4 w-4" />
              {backupInProgress ? 'Creating...' : 'Create Backup Now'}
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Backup Name</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Size</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {backups.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                          No backups found. Create one to get started.
                        </TableCell>
                      </TableRow>
                    ) : (
                      backups.map((backup) => (
                        <TableRow key={backup.backup_name}>
                          <TableCell className="font-mono text-sm">{backup.backup_name}</TableCell>
                          <TableCell>{formatDate(backup.created_at)}</TableCell>
                          <TableCell>{(backup.size_bytes / (1024 * 1024)).toFixed(2)} MB</TableCell>
                          <TableCell>
                            <Badge variant={backup.status === 'completed' ? 'default' : 'secondary'}>
                              {backup.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => downloadBackup(backup.backup_name)}
                              data-testid={`download-backup-${backup.backup_name}`}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Cancelled Accounts Tab */}
        <TabsContent value="cancelled" className="space-y-4">
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-amber-800">
                <AlertTriangle className="h-5 w-5" />
                <p>
                  Cancelled account data is retained for 6 months in Archive storage, 
                  then automatically deleted. You can manually delete earlier to save costs.
                </p>
              </div>
            </CardContent>
          </Card>

          {loading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Company ID</TableHead>
                      <TableHead>Cancelled Date</TableHead>
                      <TableHead>Retention Until</TableHead>
                      <TableHead>Files</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cancelledAccounts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                          No cancelled accounts with retained data
                        </TableCell>
                      </TableRow>
                    ) : (
                      cancelledAccounts.map((account) => (
                        <TableRow key={account.company_id}>
                          <TableCell className="font-mono text-sm">{account.company_id}</TableCell>
                          <TableCell>{formatDate(account.cancelled_at)}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4 text-gray-400" />
                              {formatDate(account.retention_until)}
                            </div>
                          </TableCell>
                          <TableCell>{account.files_count || 0}</TableCell>
                          <TableCell>
                            <Button 
                              variant="destructive" 
                              size="sm"
                              onClick={() => {
                                setSelectedAccount(account);
                                setShowDeleteModal(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Delete Now
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete Account Data
            </DialogTitle>
            <DialogDescription>
              This will permanently delete all stored data for company &quot;{selectedAccount?.company_id}&quot;.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={deleteAccountData}>
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperadminStorageManagement;
